import React, { useEffect, useState } from "react";
import { FinancialAccount, LedgerEntry, LedgerEntryKind, LedgerEntryUpsert, PaymentMethod } from "../types";
import { cashService, receivablesService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import { addDaysLocal, todayLocal } from "../utils/dates";
import "./SendDocumentModal.css";
import "../pages/Receivables.css";

interface Props {
  firmId: string;
  firmName: string;
  /** Düzenlenen kayıt (yoksa yeni) */
  entry?: LedgerEntry | null;
  /** Açılışta seçili tür */
  initialKind?: LedgerEntryKind;
  defaultCurrency?: string;
  defaultTermDays?: number;
  onClose: () => void;
  onSaved: () => void;
}

const KIND_TABS: { kind: LedgerEntryKind; label: string; credit: boolean; hint: string }[] = [
  { kind: LedgerEntryKind.Payment, label: "Tahsilat", credit: true, hint: "Firmadan alınan ödeme — borcu düşürür." },
  { kind: LedgerEntryKind.CreditNote, label: "Alacak Dekontu", credit: true, hint: "İskonto, iade, fiyat düzeltmesi — borcu düşürür." },
  { kind: LedgerEntryKind.DebitNote, label: "Borç Dekontu", credit: false, hint: "Vade farkı, ek ücret, fark faturası — borç ekler." },
  { kind: LedgerEntryKind.Invoice, label: "Fatura (elle)", credit: false, hint: "Sistem dışında kesilmiş faturanın tutarı — borç ekler. Sistemden kesilenler otomatik işlenir." },
  { kind: LedgerEntryKind.Opening, label: "Devir", credit: false, hint: "Sisteme geçiş öncesi mevcut borç bakiyesi." },
];

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: PaymentMethod.BankTransfer, label: "Havale / EFT" },
  { value: PaymentMethod.Cash, label: "Nakit" },
  { value: PaymentMethod.Cheque, label: "Çek / Senet" },
  { value: PaymentMethod.CreditCard, label: "Kredi Kartı" },
  { value: PaymentMethod.Other, label: "Diğer" },
];

const toInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : "");
const today = () => todayLocal();
const addDays = (iso: string, d: number) => addDaysLocal(iso, d);

const LedgerEntryModal: React.FC<Props> = ({
  firmId, firmName, entry, initialKind, defaultCurrency = "TRY", defaultTermDays = 30, onClose, onSaved,
}) => {
  const [kind, setKind] = useState<LedgerEntryKind>(entry?.kind ?? initialKind ?? LedgerEntryKind.Payment);
  const [amount, setAmount] = useState(entry ? String(entry.amount) : "");
  const [currency, setCurrency] = useState(entry?.currency ?? defaultCurrency);
  const [entryDate, setEntryDate] = useState(toInput(entry?.entryDate) || today());
  const [dueDate, setDueDate] = useState(toInput(entry?.dueDate));
  const [method, setMethod] = useState<PaymentMethod>(entry?.paymentMethod ?? PaymentMethod.BankTransfer);
  const [referenceNo, setReferenceNo] = useState(entry?.referenceNo ?? "");
  const [description, setDescription] = useState(entry?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Finans modülü: tahsilat hangi kasa/banka hesabına girdi
  const { hasModule } = usePermissions();
  const financeOn = hasModule("finance");
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [accountId, setAccountId] = useState<string>(entry?.financialAccountId ?? "");
  const [cashAmount, setCashAmount] = useState("");
  useEffect(() => {
    if (!financeOn) return;
    cashService.accounts(false).then(setAccounts).catch(() => setAccounts([]));
  }, [financeOn]);
  // Yeni tahsilatta para birimine uyan varsayılan hesabı öner
  useEffect(() => {
    if (!financeOn || entry || accounts.length === 0) return;
    setAccountId((cur) => cur || accounts.find((a) => a.isDefault && a.currency === currency)?.financialAccountId || "");
  }, [financeOn, entry, accounts, currency]);
  const account = accounts.find((a) => a.financialAccountId === accountId);
  const crossCurrency = !!account && account.currency !== currency;

  const tab = KIND_TABS.find((t) => t.kind === kind) ?? KIND_TABS[0];
  const isDebit = !tab.credit;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  // Borç türüne geçildiğinde vade boşsa varsayılan vadeyi öner
  useEffect(() => {
    if (isDebit && !dueDate && entryDate) setDueDate(addDays(entryDate, defaultTermDays));
  }, [isDebit, entryDate, dueDate, defaultTermDays]);

  const save = async () => {
    const n = parseFloat(amount.replace(",", "."));
    if (!n || n <= 0) return setError("Tutar sıfırdan büyük olmalıdır.");
    if (!entryDate) return setError("İşlem tarihi zorunludur.");

    const dto: LedgerEntryUpsert = {
      firmId, kind, amount: n, currency,
      entryDate,
      dueDate: isDebit ? (dueDate || null) : null,
      paymentMethod: kind === LedgerEntryKind.Payment ? method : null,
      referenceNo: referenceNo.trim() || null,
      description: description.trim() || null,
      financialAccountId: kind === LedgerEntryKind.Payment && financeOn ? (accountId || null) : null,
      cashAmount: kind === LedgerEntryKind.Payment && financeOn && crossCurrency ? (parseFloat(cashAmount.replace(",", ".")) || null) : null,
    };
    if (kind === LedgerEntryKind.Payment && financeOn && crossCurrency && !dto.cashAmount) return setError(`Hesap ${account!.currency}; hesaba giren ${account!.currency} tutarını girin.`);

    setSaving(true); setError("");
    try {
      if (entry) await receivablesService.update(entry.firmLedgerEntryId, dto);
      else await receivablesService.create(dto);
      onSaved();
      onClose();
    } catch (err: any) {
      const d = err?.response?.data;
      setError(typeof d === "string" ? d : d?.message || "Kayıt yapılamadı.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ic-overlay rc-page" onClick={() => !saving && onClose()}>
      <div className="ic-panel" style={{ maxWidth: "40rem" }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="ic-bar">
          <div className="ic-bar-left">
            <h2>{entry ? "Hareketi Düzenle" : "Cari Hareket"}</h2>
            <span className="ic-chip neutral">{firmName}</span>
          </div>
          <button className="ic-close" onClick={onClose} aria-label="Kapat" disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="ic-body">
          {error && <div className="ic-alert error">{error}</div>}

          <div className="ic-doc" style={{ padding: "1.25rem 1.5rem" }}>
            <div className="rc-field" style={{ marginBottom: "1rem" }}>
              <label>Hareket Türü</label>
              <div className="rc-kind-tabs">
                {KIND_TABS.map((t) => (
                  <button
                    key={t.kind}
                    type="button"
                    className={`rc-kind-tab ${t.credit ? "credit" : ""} ${kind === t.kind ? "active" : ""}`}
                    onClick={() => setKind(t.kind)}
                    disabled={!!entry && saving}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="rc-hint">{tab.hint}</div>
            </div>

            <div className="rc-form">
              <div className="rc-field">
                <label htmlFor="rc-amount">Tutar</label>
                <input id="rc-amount" className="ic-in num" inputMode="decimal" value={amount} placeholder="0,00"
                  autoFocus onChange={(e) => { setAmount(e.target.value); setError(""); }} />
              </div>
              <div className="rc-field">
                <label htmlFor="rc-cur">Para Birimi</label>
                <select id="rc-cur" className="ic-in" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                </select>
              </div>
              <div className="rc-field">
                <label htmlFor="rc-date">İşlem Tarihi</label>
                <input id="rc-date" type="date" className="ic-in" value={entryDate} max={today()} onChange={(e) => setEntryDate(e.target.value)} />
              </div>
              {isDebit ? (
                <div className="rc-field">
                  <label htmlFor="rc-due">Vade</label>
                  <input id="rc-due" type="date" className="ic-in" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
              ) : kind === LedgerEntryKind.Payment ? (
                <div className="rc-field">
                  <label htmlFor="rc-method">Ödeme Şekli</label>
                  <select id="rc-method" className="ic-in" value={method} onChange={(e) => setMethod(parseInt(e.target.value, 10))}>
                    {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              ) : <div />}
              {kind === LedgerEntryKind.Payment && financeOn && method === PaymentMethod.Cheque && (
                <div className="rc-field full"><div className="rc-hint">Çek/senetleri Finans &gt; Çek ve Senet ekranından girin: vade takibi, bankaya verme, ciro ve karşılıksız akışı oradan yönetilir; cari kaydı otomatik oluşur.</div></div>
              )}
              {kind === LedgerEntryKind.Payment && financeOn && (
                <div className="rc-field">
                  <label htmlFor="rc-acc">Kasa / Banka Hesabı</label>
                  <select id="rc-acc" className="ic-in" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                    <option value="">Seçilmedi — kasaya işlenmez</option>
                    {accounts.map((a) => <option key={a.financialAccountId} value={a.financialAccountId}>{a.name} · {a.currency}</option>)}
                  </select>
                </div>
              )}
              {kind === LedgerEntryKind.Payment && financeOn && crossCurrency && (
                <div className="rc-field">
                  <label htmlFor="rc-cash">Hesaba Giren Tutar ({account!.currency})</label>
                  <input id="rc-cash" className="ic-in num" inputMode="decimal" value={cashAmount} placeholder="0,00" onChange={(e) => { setCashAmount(e.target.value); setError(""); }} />
                </div>
              )}
              <div className="rc-field">
                <label htmlFor="rc-ref">Referans No</label>
                <input id="rc-ref" className="ic-in" value={referenceNo} maxLength={100}
                  placeholder={kind === LedgerEntryKind.Payment ? "Dekont / çek no" : "Fatura / belge no"}
                  onChange={(e) => setReferenceNo(e.target.value)} />
              </div>
              <div className="rc-field full">
                <label htmlFor="rc-desc">Açıklama</label>
                <input id="rc-desc" className="ic-in" value={description} maxLength={300} placeholder="İsteğe bağlı"
                  onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <footer className="ic-actions">
          <span className="ic-actions-summary">
            {isDebit ? "Borç kaydı" : "Alacak kaydı"} · {firmName}
          </span>
          <div className="ic-actions-btns">
            <button className="ic-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button className="ic-btn primary" onClick={save} disabled={saving}>
              {saving ? (<><span className="ic-spin" aria-hidden="true" />Kaydediliyor…</>) : (entry ? "Güncelle" : "Kaydet")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LedgerEntryModal;
